/**
 * The small input rules the access screens share. The server decides every
 * one of them again; these only keep a form from sending what it already
 * knows will be refused, and say why in the person's words.
 */

/** Every code the module mails is eight digits ([F-031], [F-033]). */
export const CODE_LENGTH = 8;

/** The module's floor for a password (MIN_PASSWORD_LENGTH). */
export const MIN_PASSWORD_LENGTH = 10;

/** [DO-012]: 4 to 32 grapheme clusters. */
export const DISPLAY_NAME_MIN = 4;
export const DISPLAY_NAME_MAX = 32;

/** A pasted code often carries spaces or a hyphen in the middle. */
export function normalizeCode(value: string): string {
  return value.replace(/[\s-]/g, "");
}

export function isCodeComplete(value: string): boolean {
  return new RegExp(`^\\d{${CODE_LENGTH}}$`).test(normalizeCode(value));
}

export type PasswordProblem = "short" | "mismatch";

/** The password rule the form can check itself; `repeat` only where asked twice. */
export function passwordProblem(password: string, repeat?: string): PasswordProblem | null {
  if (password.length < MIN_PASSWORD_LENGTH) return "short";
  if (repeat !== undefined && repeat !== password) return "mismatch";
  return null;
}

/** Grapheme clusters, as [DO-012] counts - not bytes, not code points. */
export function graphemeLength(value: string): number {
  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  let n = 0;
  for (const _cluster of segmenter.segment(value)) n += 1;
  return n;
}

/** The module's normal form: NFC, inner whitespace collapsed, edges trimmed. */
export function normalizeDisplayName(value: string): string {
  return value.normalize("NFC").replace(/\s+/gu, " ").trim();
}

export type DisplayNameProblem = "short" | "long";

/**
 * The length half of [DO-012]. The character rules (one script, no invisible
 * characters, the allowed punctuation) and the uniqueness stay the server's:
 * its sentence is precise, and a second copy here would drift from it.
 */
export function displayNameProblem(value: string): DisplayNameProblem | null {
  const length = graphemeLength(normalizeDisplayName(value));
  if (length < DISPLAY_NAME_MIN) return "short";
  if (length > DISPLAY_NAME_MAX) return "long";
  return null;
}

/** i18n keys of the problems above - one place, so a screen cannot misspell one. */
export const PASSWORD_PROBLEM_KEY: Record<PasswordProblem, string> = {
  short: "forms.password.short",
  mismatch: "forms.password.mismatch",
};

export const DISPLAY_NAME_PROBLEM_KEY: Record<DisplayNameProblem, string> = {
  short: "forms.displayName.short",
  long: "forms.displayName.long",
};

/** A plausible mail address - the server checks again. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
